"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Vector3 = void 0;
var Vector3 = /** @class */ (function () {
    /**
     * Initializes a new instance of the Vector3 class.
     * @param x - The x coefficient of the vector.
     * @param y - The y coefficient of the vector.
     * @param z - The z coefficient of the vector.
     */
    function Vector3(x, y, z) {
        this.x = x;
        this.y = y;
        this.z = z;
    }
    /** Gets the square of the vectors length (high performance). */
    Vector3.prototype.squaredLength = function () {
        return (this.x * this.x) + (this.y * this.y) + (this.z * this.z);
    };
    /** Gets the length of the vector (see SquaredLength for a high performance option which can be used in vector comparison tasks). */
    Vector3.prototype.length = function () {
        var lengthSquared = this.squaredLength();
        return Math.sqrt(lengthSquared);
    };
    /** Normalize a vector to make its length equal to 1 */
    Vector3.prototype.normalize = function () {
        var squaredLength = this.squaredLength();
        if (squaredLength > 0 && squaredLength != 1) {
            var length = Math.sqrt(squaredLength);
            this.x /= length;
            this.y /= length;
            this.z /= length;
        }
    };
    /** Return a copy of the vector that has been normalized.
     * @returns - A copy of the vector that has been normalized.
     */
    Vector3.prototype.normalized = function () {
        var vector = new Vector3(this.x, this.y, this.z);
        vector.normalize();
        return vector;
    };
    /** Adds a vector to the current vector to get another vector.
     * @param rhs - The vector to add to the current vector
     * @returns - A new vector whose x/y/z coefficients equal the sum of the current vector and the supplied vector.
     */
    Vector3.prototype.add = function (rhs) {
        return new Vector3(this.x + rhs.x, this.y + rhs.y, this.z + rhs.z);
    };
    /** Subtracts one vector from another vector.
     * @param rhs - The vector to subtract from the current vector.
     * @returns - A new vector whose x/y/z coefficients equal the current vector's x/y/z minus the supplied vector's x/y/z.
     */
    Vector3.prototype.subtract = function (rhs) {
        return new Vector3(this.x - rhs.x, this.y - rhs.y, this.z - rhs.z);
    };
    /** Multiplies a vector by a floating point value, changing its length.
     * @param scalar - The value to multiply each of the vector's coefficients by.
     * @returns - A new vector whose x/y/z coefficients equal the current vector's x/y/z multiplied by the supplied scalar value.
     */
    Vector3.prototype.multiplyByScalar = function (scalar) {
        return new Vector3(this.x * scalar, this.y * scalar, this.z * scalar);
    };
    /** Divides a vector by a floating point value, changing its length.
     * @param scalar - The value to divide each of the vector's coefficients by.
     * @returns - A new vector whose x/y/z coefficients equal the current vector's x/y/z divided by the supplied scalar value.
     */
    Vector3.prototype.divideByScalar = function (scalar) {
        return new Vector3(this.x / scalar, this.y / scalar, this.z / scalar);
    };
    /** Produce the dot product of the vector with another vector, which has many interesting uses (most notably, it's the Cos(theta)
     *  of the angle between the two vectors).
     * @param rhs - The other vector to dot product with the current vector.
     * @returns - The dot product of this vector with another vector.
     */
    Vector3.prototype.dot = function (rhs) {
        return (this.x * rhs.x) + (this.y * rhs.y) + (this.z * rhs.z);
    };
    /** Calculate the 'cross product' of two vectors, which gives a new vector that is at right-angles with the plane on which
     *   this vector and the other vector lie on.
     * @param rhs - The other vector to use in the cross product.
     * @param returns - A new vector that is at right-angles to the two supplied vectors.
     */
    Vector3.prototype.cross = function (rhs) {
        var x = (this.y * rhs.z) - (rhs.y * this.z);
        var y = (this.z * rhs.x) - (rhs.z * this.x);
        var z = (this.x * rhs.y) - (rhs.x * this.y);
        return new Vector3(x, y, z);
    };
    /** Calculates the distance between two points in 3D space.
     * @param start - The start point in 3D space.
     * @param end - The end point in 3D space.
     * @returns Returns the straight-line distance between the two points.
     */
    Vector3.distance = function (start, end) {
        var delta = start.subtract(end);
        return delta.length();
    };
    /**
     * Linear Interpolation between two vectors by a given factor.
     * @param start - The starting vector of the interpolation
     * @param end - The ending vector of the interpolation
     * @param amount -  value between 0 and 1, where 0 would return start and 1 would return end
     * @returns Returns a vector that is amount between start and end.
     */
    Vector3.lerp = function (start, end, amount) {
        var delta = end.subtract(start);
        return start.add(delta.multiplyByScalar(amount));
    };
    return Vector3;
}());
exports.Vector3 = Vector3;
//# sourceMappingURL=Vector3.js.map